import config from '@/config';
import { SCREEN_ORIENTATION } from '@/constants';
import { logger } from '@/utils/screenEventsLogger';

export function getTizenDeviceInfo() {
  return new Promise(function (resolve, reject) {
    const deviceInfo = {
      status: 2,
      network: {
        type: null,
        ip: null,
        gateway: null,
        mac: null,
        netmask: null,
        dns: null,
        method: null,
      },
      storage: {
        internal: {
          total: null,
          free: null,
          used: null,
        },
      },
      datetime: {
        current: new Date().toISOString(),
        timezone: null,
      },
      platformInfo: {
        os: null,
        osVersion: null,
        serialNumber: null,
        modelName: null,
        firmwareVersion: null,
        sdkVersion: null,
      },
      systemUsage: {
        memory: {
          total: null,
          free: null,
          used: null,
          buffer: null,
          cached: null,
        },
      },
      screenOrientation: null,
    };

    if (typeof tizen === 'undefined' || !tizen.systeminfo) {
      logger.addLog('Tizen API not available, returning mock data');
      resolve(deviceInfo);
      return;
    }

    let pendingRequests = 0;
    let completedRequests = 0;
    let timeoutId = null;

    function checkCompletion() {
      completedRequests++;
      if (completedRequests >= pendingRequests) {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        resolve(deviceInfo);
      }
    }

    function handleError(error, propertyName) {
      logger.addLog('Failed to get ' + propertyName + ': ' + error.message);
      checkCompletion();
    }

    function detectActualOrientation(sensorOrientation) {
      try {
        if (typeof screen === 'undefined') {
          logger.addLog('Screen object not available, using sensor reading');
          return sensorOrientation || 'LANDSCAPE';
        }
        const screenWidth = screen.width;
        const screenHeight = screen.height;
        if (!screenWidth || !screenHeight) {
          logger.addLog('Invalid screen dimensions, using sensor reading');
          return sensorOrientation || 'LANDSCAPE';
        }
        const isLandscape = screenWidth > screenHeight;
        const orientationInfo = {
          sensor: sensorOrientation,
          actual: isLandscape ? 'LANDSCAPE' : 'PORTRAIT',
          dimensions: {
            width: screenWidth,
            height: screenHeight,
          },
        };
        return orientationInfo.actual;
      } catch (e) {
        logger.addLog('Could not detect screen dimensions, using sensor reading or fallback');
        return sensorOrientation || 'LANDSCAPE';
      }
    }

    function tryDeviceCapabilities() {
      // Tizen 4 priority
      if (typeof b2bapis !== 'undefined' && b2bapis.b2bcontrol) {
        try {
          const serialNumber = b2bapis.b2bcontrol.getSerialNumber();
          if (serialNumber && typeof serialNumber === 'string' && serialNumber.length > 0) {
            deviceInfo.platformInfo.serialNumber = serialNumber;
            checkCompletion();
            return;
          }
        } catch (e) {
          // Continue to fallback methods
        }
      }
      // Priority 1: Try Samsung SystemControl API getSerialNumber() first
      if (typeof webapis !== 'undefined' && webapis.systemcontrol) {
        try {
          const serialNumber = webapis.systemcontrol.getSerialNumber();
          if (serialNumber && typeof serialNumber === 'string' && serialNumber.length > 0) {
            deviceInfo.platformInfo.serialNumber = serialNumber;
            checkCompletion();
            return;
          }
        } catch (e) {
          // Continue to fallback methods
        }
      } else {
      }

      // Priority 2: Get DUID (Device Unique Identifier) from Samsung webapis productinfo
      if (typeof webapis !== 'undefined' && webapis.productinfo) {
        try {
          if (webapis.productinfo.getDuid) {
            const duid = webapis.productinfo.getDuid();
            if (duid && typeof duid === 'string' && duid.length > 0) {
              deviceInfo.platformInfo.serialNumber = duid;
              checkCompletion();
              return;
            }
          }
        } catch (e) {
          // Continue to fallback methods
        }
      }

      // Priority 3: Fallback to Tizen device capabilities (TizenID, etc.)
      try {
        if (tizen && tizen.systeminfo && typeof tizen.systeminfo.getCapability === 'function') {
          const deviceCapabilities = [
            'http://tizen.org/system/tizenid',
            'http://tizen.org/system/duid',
            'http://tizen.org/system/device_uuid',
          ];

          for (let i = 0; i < deviceCapabilities.length; i++) {
            try {
              const deviceId = tizen.systeminfo.getCapability(deviceCapabilities[i]);
              if (deviceId && typeof deviceId === 'string' && deviceId.length > 0) {
                deviceInfo.platformInfo.serialNumber = deviceId;
                break;
              }
            } catch (capError) {
              continue;
            }
          }
        }
      } catch (e) {
        // Capability methods not available
      }

      checkCompletion();
    }

    // Set a timeout to ensure we don't wait indefinitely
    timeoutId = setTimeout(function () {
      logger.addLog('Device info collection timed out, returning partial data');
      resolve(deviceInfo);
    }, 10000);

    pendingRequests++;
    try {
      if (tizen && tizen.systeminfo && typeof tizen.systeminfo.getPropertyValue === 'function') {
        tizen.systeminfo.getPropertyValue(
          'BUILD',
          function (build) {
            deviceInfo.platformInfo.modelName = build.model || null;
            deviceInfo.platformInfo.firmwareVersion =
              (build.buildVersion && build.buildVersion.slice(-50)) || null;
            deviceInfo.platformInfo.os = 'Tizen';
            checkCompletion();
          },
          function (error) {
            handleError(error, 'BUILD');
          },
        );
      } else {
        handleError(new Error('Tizen SystemInfo API not available'), 'BUILD');
      }
    } catch (e) {
      handleError(e, 'BUILD');
    }

    // Get NETWORK information
    pendingRequests++;
    try {
      tizen.systeminfo.getPropertyValue(
        'NETWORK',
        function (network) {
          deviceInfo.network.type = network.networkType || null;
          checkCompletion();
        },
        function (error) {
          handleError(error, 'NETWORK');
        },
      );
    } catch (e) {
      handleError(e, 'NETWORK');
    }

    try {
      if (typeof webapis !== 'undefined' && webapis.network) {
        if (typeof webapis.network.getDns === 'function') {
          deviceInfo.network.dns = webapis.network.getDns();
        }
        if (typeof webapis.network.getGateway === 'function') {
          deviceInfo.network.gateway = webapis.network.getGateway();
        }
        if (typeof webapis.network.getSubnetMask === 'function') {
          deviceInfo.network.netmask = webapis.network.getSubnetMask();
        }
      }
    } catch (e) {
      logger.addLog('Error getting network info from webapis.network: ' + e.message);
    }

    // Get WIFI_NETWORK information if available
    pendingRequests++;
    try {
      tizen.systeminfo.getPropertyValue(
        'WIFI_NETWORK',
        function (wifi) {
          if (wifi.status === 'ON') {
            deviceInfo.network.ip = deviceInfo.network.ip || wifi.ipAddress || null;
            deviceInfo.network.gateway = deviceInfo.network.gateway || wifi.gateway || null;
            deviceInfo.network.mac = deviceInfo.network.mac || wifi.macAddress || null;
            deviceInfo.network.netmask = deviceInfo.network.netmask || wifi.subnetMask || null;
            deviceInfo.network.dns = deviceInfo.network.dns || wifi.dns || null;
            deviceInfo.network.method = deviceInfo.network.method || wifi.ipMode || null;
          }
          checkCompletion();
        },
        function (error) {
          handleError(error, 'WIFI_NETWORK');
        },
      );
    } catch (e) {
      handleError(e, 'WIFI_NETWORK');
    }

    // Get ETHERNET_NETWORK information if available
    pendingRequests++;
    try {
      tizen.systeminfo.getPropertyValue(
        'ETHERNET_NETWORK',
        function (ethernet) {
          if (ethernet.status === 'CONNECTED') {
            deviceInfo.network.ip = deviceInfo.network.ip || ethernet.ipAddress || null;
            deviceInfo.network.gateway = deviceInfo.network.gateway || ethernet.gateway || null;
            deviceInfo.network.mac = deviceInfo.network.mac || ethernet.macAddress || null;
            deviceInfo.network.netmask = deviceInfo.network.netmask || ethernet.subnetMask || null;
            deviceInfo.network.dns = deviceInfo.network.dns || ethernet.dns || null;
            deviceInfo.network.method = deviceInfo.network.method || ethernet.ipMode || null;
          }
          checkCompletion();
        },
        function (error) {
          handleError(error, 'ETHERNET_NETWORK');
        },
      );
    } catch (e) {
      handleError(e, 'ETHERNET_NETWORK');
    }

    // Get STORAGE information
    pendingRequests++;
    try {
      tizen.systeminfo.getPropertyValue(
        'STORAGE',
        function (storage) {
          if (storage.units && storage.units.length > 0) {
            for (let i = 0; i < storage.units.length; i++) {
              const unit = storage.units[i];
              if (unit.type === 'INTERNAL') {
                deviceInfo.storage.internal.total = unit.capacity.toString();
                deviceInfo.storage.internal.free = unit.availableCapacity.toString();
                deviceInfo.storage.internal.used = (
                  unit.capacity - unit.availableCapacity
                ).toString();
                break;
              }
            }
          }
          checkCompletion();
        },
        function (error) {
          handleError(error, 'STORAGE');
        },
      );
    } catch (e) {
      handleError(e, 'STORAGE');
    }

    // Get MEMORY information
    pendingRequests++;
    try {
      if (
        tizen &&
        tizen.systeminfo &&
        typeof tizen.systeminfo.getTotalMemory === 'function' &&
        typeof tizen.systeminfo.getAvailableMemory === 'function'
      ) {
        const totalMemory = tizen.systeminfo.getTotalMemory();
        const availableMemory = tizen.systeminfo.getAvailableMemory();
        const usedMemory = totalMemory - availableMemory;

        deviceInfo.systemUsage.memory.total = totalMemory;
        deviceInfo.systemUsage.memory.free = availableMemory;
        deviceInfo.systemUsage.memory.used = usedMemory;
        // Note: buffer and cached are not available in Tizen API
        checkCompletion();
      } else {
        handleError(new Error('Tizen memory API not available'), 'MEMORY');
      }
    } catch (e) {
      handleError(e, 'MEMORY');
    }

    // Get DEVICE_ORIENTATION information
    pendingRequests++;
    try {
      tizen.systeminfo.getPropertyValue(
        'DEVICE_ORIENTATION',
        function (orientation) {
          // Detect actual orientation based on screen dimensions
          const actualOrientation = detectActualOrientation(orientation.status).toLowerCase();
          deviceInfo.screenOrientation = actualOrientation;
          checkCompletion();
        },
        function (error) {
          handleError(error, 'DEVICE_ORIENTATION');
        },
      );
    } catch (e) {
      handleError(e, 'DEVICE_ORIENTATION');
    }

    // Get TIMEZONE information
    pendingRequests++;
    try {
      if (tizen && tizen.time && typeof tizen.time.getLocalTimezone === 'function') {
        deviceInfo.datetime.timezone = tizen.time.getLocalTimezone();
      }
      checkCompletion();
    } catch (e) {
      handleError(e, 'TIMEZONE');
    }

    // Try to get additional platform info
    pendingRequests++;
    try {
      try {
        if (tizen && tizen.systeminfo && typeof tizen.systeminfo.getCapability === 'function') {
          deviceInfo.platformInfo.osVersion =
            tizen.systeminfo.getCapability('http://tizen.org/feature/platform.version') || null;
        }
      } catch (e) {
        // Capability not available
      }

      try {
        if (tizen && tizen.systeminfo && typeof tizen.systeminfo.getCapability === 'function') {
          deviceInfo.platformInfo.sdkVersion =
            tizen.systeminfo.getCapability('http://tizen.org/feature/platform.core.api.version') ||
            null;
        }
      } catch (e) {
        // Capability not available
      }

      checkCompletion();
    } catch (e) {
      handleError(e, 'SYSTEM_INFO');
    }

    // Get device capabilities and DUID
    pendingRequests++;
    try {
      tryDeviceCapabilities();
    } catch (e) {
      handleError(e, 'DEVICE_CAPABILITIES');
    }
  });
}

export function captureTizenScreenshot(callback) {
  const readAndSendFileAsBase64 = function (val) {
    try {
      if (typeof tizen === 'undefined' || !tizen.filesystem) {
        callback(null);
        return;
      }

      const resolvedPath = typeof val === 'string' ? val.replace(/^file:\/\//, '') : val;

      tizen.filesystem.resolve(
        resolvedPath,
        (file) => {
          try {
            file.openStream(
              'r',
              (stream) => {
                try {
                  let base64 = null;
                  if (typeof stream.readBase64 === 'function') {
                    const len =
                      typeof file.fileSize === 'number' ? file.fileSize : stream.bytesAvailable;
                    base64 = stream.readBase64(len);
                  } else {
                    const len =
                      typeof file.fileSize === 'number' ? file.fileSize : stream.bytesAvailable;
                    const bytes = stream.readBytes(len);
                    let binary = '';
                    for (let i = 0; i < bytes.length; i++) {
                      binary += String.fromCharCode(bytes[i]);
                    }
                    base64 = btoa(binary);
                  }
                  stream.close();
                  callback(base64);
                } catch (e) {
                  try {
                    stream.close();
                  } catch (closeErr) {}
                  callback(null);
                }
              },
              () => {
                callback(null);
              },
            );
          } catch (e) {
            callback(null);
          }
        },
        () => {
          callback(null);
        },
        'r',
      );
    } catch (e) {
      callback(null);
    }
  };

  const onSuccess = function (val) {
    readAndSendFileAsBase64(val);
  };
  const onError = function (error) {
    callback(null);
  };
  // First try for Tizen 6.5+
  if (
    typeof webapis !== 'undefined' &&
    webapis.systemcontrol &&
    typeof webapis.systemcontrol.captureScreen === 'function'
  ) {
    webapis.systemcontrol.captureScreen();
    readAndSendFileAsBase64('/tmp/ScreenCapture.jpg');
  }
  // Then try for Tizen 4+
  else if (
    typeof b2bapis !== 'undefined' &&
    b2bapis.b2bcontrol &&
    typeof b2bapis.b2bcontrol.captureScreen === 'function'
  ) {
    b2bapis.b2bcontrol.captureScreen(onSuccess, onError);
  } else {
    callback(null);
  }
}

export function restartTizenScreen() {
  try {
    // First try for Tizen 6.5+
    if (
      typeof webapis !== 'undefined' &&
      webapis.systemcontrol &&
      typeof webapis.systemcontrol.rebootDevice === 'function'
    ) {
      webapis.systemcontrol.rebootDevice();
    }
    // Then try for Tizen 4+
    else if (
      typeof b2bapis !== 'undefined' &&
      b2bapis.b2bcontrol &&
      typeof b2bapis.b2bcontrol.rebootDevice === 'function'
    ) {
      const onSuccess = function (val) {
        logger.addLog('Screen reboot successful: ' + val);
      };
      const onError = function (error) {
        logger.addLog('Screen reboot failed: ' + error);
      };
      b2bapis.b2bcontrol.rebootDevice(onSuccess, onError);
    }
  } catch (e) {
    logger.addLog('Failed to restart Tizen screen: ' + e);
  }
}

export function getTizenVersion() {
  try {
    if (tizen && tizen.systeminfo && typeof tizen.systeminfo.getCapability === 'function') {
      const tizen_version = Number(
        tizen.systeminfo.getCapability('http://tizen.org/feature/platform.version'),
      );
      return tizen_version;
    }
  } catch {
    return undefined;
  }
}

export function changeTizenOrientation(orientation) {
  try {
    const tizenApps = {
      landscape: {
        '6.5+': 'SSSP',
        '4.0+': 'SSSP4',
      },
      portrait: {
        '6.5+': 'SSSPPortrait',
        '4.0+': 'SSSP4Portrait',
      },
    };
    const tizenVersion = getTizenVersion();

    let requiredVersion, requiredOrientation;
    if (isNaN(tizenVersion) || tizenVersion < 6.5) {
      requiredVersion = '4.0+';
    } else {
      requiredVersion = '6.5+';
    }

    if (
      orientation === SCREEN_ORIENTATION.LANDSCAPE ||
      orientation === SCREEN_ORIENTATION.REVERSE_LANDSCAPE
    ) {
      requiredOrientation = 'landscape';
    } else {
      requiredOrientation = 'portrait';
    }

    let address = `${config.tizenAppBaseURL}${tizenApps[requiredOrientation][requiredVersion]}`;
    //First try for Tizen 6.5+
    if (
      typeof webapis !== 'undefined' &&
      webapis.systemcontrol &&
      typeof webapis.systemcontrol.setCustomAppInfo === 'function'
    ) {
      const info = {
        downloadServerAddress: address,
        connectionTimeout: '30SEC',
      };
      webapis.systemcontrol.setCustomAppInfo(info);
      return true;
    }
    //Then try for Tizen 4+
    if (
      typeof b2bapis !== 'undefined' &&
      b2bapis.b2bcontrol &&
      typeof b2bapis.b2bcontrol.setURLLauncherAddress === 'function'
    ) {
      const onSuccess = function (val) {
        logger.addLog('[setURLLauncherAddress] success : ' + val);
      };
      const onError = function (error) {
        logger.addLog(
          '[setURLLauncherAddress] code :' +
            error.code +
            ' error name: ' +
            error.name +
            ' message ' +
            error.message,
        );
      };
      b2bapis.b2bcontrol.setURLLauncherAddress(address, onSuccess, onError);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

export function updateTizenFirmware(firmwareDetails) {
  if (!firmwareDetails) {
    logger.addLog('No firmware details provided');
    return;
  }
  const { swUrl, swVersion, softwareId, swuFileName, swTotalBytes } = firmwareDetails;
  if (!swUrl || !swVersion || !softwareId || !swuFileName || !swTotalBytes) {
    logger.addLog('Incomplete FW information');
    return;
  }

  //for tizen 4
  if (
    typeof b2bapis !== 'undefined' &&
    b2bapis.b2bcontrol &&
    typeof b2bapis.b2bcontrol.updateFirmware === 'function'
  ) {
    const onSuccess = function (val) {
      logger.addLog('Firmware update successful');
    };
    const onError = function (error) {
      logger.addLog(
        '[updateFirmware] code :' +
          error.code +
          ' error name: ' +
          error.name +
          ' message ' +
          error.message,
      );
    };
    b2bapis.b2bcontrol.updateFirmware(
      softwareId,
      swuFileName,
      swVersion,
      swUrl,
      swTotalBytes,
      onSuccess,
      onError,
    );
  }

  // for tizen 6.5+
  if (
    typeof webapis !== 'undefined' &&
    webapis.systemcontrol &&
    typeof webapis.systemcontrol.updateFirmware === 'function'
  ) {
    webapis.systemcontrol.updateFirmware(softwareId, swuFileName, swVersion, swUrl, swTotalBytes);
  }
}

function setTimerTizen65(timerType, timerIndex, hour, min, days, setup = 'TIMER_MANUAL') {
  const timerProfile = {
    timerID: `TIMER${timerIndex}`,
    time: `${hour}:${min}`,
    setup: setup,
    volume: 10,
    manual: days,
  };

  const methodName = timerType === 'on' ? 'setOnTimer' : 'setOffTimer';

  try {
    webapis.timer[methodName](timerProfile);
    logger.addLog(`${timerType.toUpperCase()} timer ${timerIndex} set successfully`);
  } catch (e) {
    logger.addLog(`[${methodName}] exception [${e.code}] name: ${e.name} message: ${e.message}`);
  }
}

function setTimerTizen4(timerType, timerIndex, hour, min, days, setup = 'TIMER_MANUAL') {
  const onSuccess = function (val) {
    logger.addLog(`[set${timerType === 'on' ? 'On' : 'Off'}TimerRepeat] success: ${val}`);
  };
  const onError = function (error) {
    logger.addLog(
      `[set${timerType === 'on' ? 'On' : 'Off'}TimerRepeat] code: ${error.code} error name: ${
        error.name
      } message: ${error.message}`,
    );
  };

  const timerID = `TIMER${timerIndex}`;
  const time = `${hour}:${min}`;
  const daysString = days.join(':');

  const methodName = timerType === 'on' ? 'setOnTimerRepeat' : 'setOffTimerRepeat';
  b2bapis.b2bcontrol[methodName](timerID, time, setup, daysString, onSuccess, onError);
}

function getTimerTizen65(timerType, timerID) {
  const methodName = timerType === 'on' ? 'getOnTimer' : 'getOffTimer';
  let timer = null;

  try {
    timer = webapis.timer[methodName](timerID);
  } catch (e) {
    logger.addLog(`[${methodName}] exception [${e.code}] name: ${e.name} message: ${e.message}`);
  }

  if (timer !== null) {
    logger.addLog(`[${methodName}] Timer found: ${timer}`);
  }

  return timer;
}

function getTimerTizen4(timerType, timerID) {
  const methodName = timerType === 'on' ? 'getOnTimerRepeat' : 'getOffTimerRepeat';
  let timer = null;

  try {
    timer = b2bapis.b2bcontrol[methodName](timerID);
  } catch (e) {
    logger.addLog(`[${methodName}] exception [${e.code}] name: ${e.name} message: ${e.message}`);
  }

  if (timer !== null) {
    logger.addLog(`[${methodName}] Timer found: ${timer}`);
  }

  return timer;
}

function disableDeletedTimers(scheduleCount, isTizen65Available, isTizen4Available) {
  let timerIndex = scheduleCount + 1;
  const timerTypes = ['on', 'off'];

  timerTypes.forEach((timerType) => {
    let currentIndex = timerIndex;
    let timer = null;
    let depth = 0;

    do {
      depth++;
      const timerID = `TIMER${currentIndex}`;

      if (isTizen65Available) {
        timer = getTimerTizen65(timerType, timerID);
        if (!!timer) {
          setTimerTizen65(timerType, currentIndex, '00', '00', [], 'TIMER_OFF');
          logger.addLog(`Disabled ${timerType.toUpperCase()} timer ${currentIndex}`);
        }
      } else if (isTizen4Available) {
        timer = getTimerTizen4(timerType, timerID);
        if (!!timer) {
          setTimerTizen4(timerType, currentIndex, '00', '00', [], 'TIMER_OFF');
          logger.addLog(`Disabled ${timerType.toUpperCase()} timer ${currentIndex}`);
        }
      } else {
        break;
      }

      currentIndex++;
    } while (!!timer && depth < 10);
  });
}

export function updateTizenSchedule(scheduleDetails) {
  const isTizen65Available =
    typeof webapis !== 'undefined' &&
    webapis.timer &&
    typeof webapis.timer.setOnTimer === 'function' &&
    typeof webapis.timer.setOffTimer === 'function' &&
    typeof webapis.timer.getOnTimer === 'function' &&
    typeof webapis.timer.getOffTimer === 'function';

  const isTizen4Available =
    typeof b2bapis !== 'undefined' &&
    b2bapis.b2bcontrol &&
    typeof b2bapis.b2bcontrol.setOnTimerRepeat === 'function' &&
    typeof b2bapis.b2bcontrol.setOffTimerRepeat === 'function' &&
    typeof b2bapis.b2bcontrol.getOnTimer === 'function' &&
    typeof b2bapis.b2bcontrol.getOffTimer === 'function';

  if (!scheduleDetails || !Array.isArray(scheduleDetails)) {
    logger.addLog('Invalid or missing schedule details');
    disableDeletedTimers(0, isTizen65Available, isTizen4Available);
    return;
  }

  scheduleDetails.forEach((schedule, index) => {
    const timerIndex = index + 1;

    if (schedule.on && schedule.on.hour && schedule.on.min && schedule.on.days) {
      if (isTizen65Available) {
        setTimerTizen65('on', timerIndex, schedule.on.hour, schedule.on.min, schedule.on.days);
      } else if (isTizen4Available) {
        setTimerTizen4('on', timerIndex, schedule.on.hour, schedule.on.min, schedule.on.days);
      }
    }

    if (schedule.off && schedule.off.hour && schedule.off.min && schedule.off.days) {
      if (isTizen65Available) {
        setTimerTizen65('off', timerIndex, schedule.off.hour, schedule.off.min, schedule.off.days);
      } else if (isTizen4Available) {
        setTimerTizen4('off', timerIndex, schedule.off.hour, schedule.off.min, schedule.off.days);
      }
    }
  });

  disableDeletedTimers(scheduleDetails.length, isTizen65Available, isTizen4Available);
}

export function changeTizenTimezone(timezoneDetails) {
  if (
    !timezoneDetails ||
    !timezoneDetails.ntpServer ||
    !timezoneDetails.settings ||
    timezoneDetails.type !== 'tizen'
  ) {
    logger.addLog('Invalid or missing timezone details');
    return;
  }

  try {
    if (
      typeof webapis !== 'undefined' &&
      webapis.timer &&
      typeof webapis.timer.setNTP === 'function'
    ) {
      const ntpInfo = {
        use: 'ON',
        address: timezoneDetails.ntpServer,
        timeZone: timezoneDetails.settings,
      };
      webapis.timer.setNTP(ntpInfo);
      logger.addLog(`Timezone changed to ${timezoneDetails.settings}`);
    } else if (typeof b2bapis !== 'undefined' && b2bapis.b2bcontrol) {
      if (typeof b2bapis.b2bcontrol.setCurrentTimeZone === 'function') {
        const onSuccess = () => {
          logger.addLog(`Timezone changed to ${timezoneDetails.settings}`);
        };
        const onError = (error) => {
          logger.addLog('ERROR: Timezone change failed - ' + error.message);
        };
        b2bapis.b2bcontrol.setCurrentTimeZone(timezoneDetails.settings, onSuccess, onError);
      }

      if (typeof b2bapis.b2bcontrol.setNTPServerAddress === 'function') {
        const onSuccess = () => {
          logger.addLog(`NTP server address set to ${timezoneDetails.ntpServer}`);
        };
        const onError = (error) => {
          logger.addLog('ERROR: NTP server address set failed - ' + error.message);
        };
        b2bapis.b2bcontrol.setNTPServerAddress(timezoneDetails.ntpServer, onSuccess, onError);
      }
    } else {
      logger.addLog('ERROR: Timezone change not supported on this Tizen version');
    }
  } catch (error) {
    logger.addLog('ERROR: Timezone change failed - ' + error.message);
  }
}

export function isTizenConnectedToInternet() {
  // Native connection type isn't reliable on Tizen 6.5+, use the browser status there.
  if (getTizenVersion() >= 6.5) {
    return navigator.onLine;
  }

  if (
    typeof webapis === 'undefined' ||
    !webapis.network ||
    typeof webapis.network.getActiveConnectionType !== 'function'
  ) {
    throw new Error('Tizen network API is not available');
  }

  const connectionType = webapis.network.getActiveConnectionType();
  return connectionType !== webapis.network.NetworkActiveConnectionType.DISCONNECTED;
}
